<script setup lang="ts">
import BuyButton from "../common/BuyButton.vue";
import FavoriteButton from "./FavoriteButton.vue";
import ProfileCoverPopover from "./ProfileCoverPopover.vue";
import ProfileLanguageToggle from "./ProfileLanguageToggle.vue";
import { ref, watch, PropType } from "vue";
import { Trainer, TranslatedProduct } from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";
import ConfigureProject from "../projects/configure-tabs/ConfigureProject.vue";
import { BENCHMARK_JOBMATCH } from "../../utils/ProjectUtils.ts";

const { t } = useI18n();

const props = defineProps({
    products: {
        type: Object as PropType<Record<string, TranslatedProduct[]>>,
        required: true,
    },
    layout: {
        type: String as PropType<"list" | "grid" | undefined>,
        default: "list",
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const layout = ref(props.layout);

const emit = defineEmits(["update"]);

function handleUpdate(product: string) {
    emit("update", true, product);
}

watch(
    () => props.layout,
    (newLayout: "list" | "grid" ) => {
        layout.value = newLayout;
    },
);

</script>

<template>
    <div v-for="(productGroup, modelName) in props.products" :key="modelName" class="mb-8">
        <DataView
            :value="productGroup"
            :layout="layout"
            data-key="id"
            :pt="{ header: { style: 'background: var(--p-secondary-500); padding: 0.75rem 1rem;' } }"
        >
            <template #header>
                <div class="flex justify-between items-center">
                    <h2 class="m-0 ml-4 text-white">{{ modelName }}</h2>
                </div>
            </template>
            <template #list="slotProps">
                <div
                    v-for="item in slotProps.items"
                    :key="item.productNo"
                    class="product-card badge-container"
                    :id="'data-view-purchased-products_data-view_' + item.productNo + '_card'"
                >
                    <OverlayBadge class="mt-2">
                        <div class="flex justify-between p-overlay-badge p-6">
                            <div class="flex flex-col flex-1 mr-4">
                                <div class="flex flex-wrap items-center gap-2">
                                    <div class="flex items-center gap-1">
                                        <h3
                                            class="mt-0 mb-0 text-2xl font-bold text-content"
                                            :id="'data-view-purchased-products_data-view_' + item.productNo + '_name'"
                                        >
                                            {{ item.productName }}
                                        </h3>
                                        <ProfileCoverPopover :product-no="item.productNo" :product-name="item.productName" />
                                    </div>
                                    <Tag
                                        v-if="item.quantity > 0 && item.productNo !== BENCHMARK_JOBMATCH"
                                        :id="'data-view-purchased-products_badge_' + item.productNo + '_amount'"
                                        severity="info"
                                        class="whitespace-nowrap"
                                        style="cursor: default"
                                    >{{ t('profile-overview.product-card.purchased-amount', { quantity: item.quantity }, item.quantity) }}</Tag>
                                </div>
                                <FavoriteButton
                                    v-if="item.quantity === 0"
                                    filled
                                    :action="item.quantity === 0 ? 'remove' : 'add'"
                                    :product="item.productNo"
                                    @update="handleUpdate(item.productNo)"
                                    class="p-overlay-badge p-badge p-0"
                                />
                                <ProfileLanguageToggle
                                    :product-no="item.productNo"
                                    :profile-languages="item.profileLanguages"
                                    :questionnaire-languages="item.questionnaireLanguages"
                                    :trainer-language="trainer.language"
                                    :toggle-id="'data-view-purchased-products_div_' + item.productNo + '_language-toggle'"
                                    :fieldset-id="'data-view-purchased-products_fieldset_' + item.productNo + '_languages'"
                                />
                            </div>
                            <div class="flex gap-2 flex-col">
                                <BuyButton
                                    v-if="item.productNo !== BENCHMARK_JOBMATCH"
                                    :product="item.productNo"
                                    :product-name="item.productName"
                                    :trainer="props.trainer"
                                    :shop-link="item.shopLink"
                                    @update="handleUpdate(item.productNo)"
                                />
                                <ConfigureProject :product="item" :trainer="props.trainer" />
                            </div>
                        </div>
                    </OverlayBadge>
                </div>
            </template>

            <template #grid="slotProps">
                <div
                    v-for="item in slotProps.items"
                    :key="item.productNo"
                    class="p-overlay-badge product-card flex"
                    :id="'data-view-purchased-products_data-view_' + item.productNo + '_card'"
                >
                    <OverlayBadge class="mt-2 w-full">
                        <div class="flex flex-col justify-between p-6 border-border-ui bg-surface">
                            <div class="flex flex-col justify-between items-center gap-2">
                                <div class="flex items-center justify-center gap-1">
                                    <h3
                                        class="m-0 text-2xl font-bold text-content text-center"
                                        :id="'data-view-purchased-products_data-view_' + item.productNo + '_name'"
                                    >
                                        {{ item.productName }}
                                    </h3>
                                    <ProfileCoverPopover :product-no="item.productNo" :product-name="item.productName" />
                                </div>
                                <Tag
                                    v-if="item.quantity > 0 && item.productNo !== BENCHMARK_JOBMATCH"
                                    :id="'data-view-purchased-products_badge_' + item.productNo + '_amount'"
                                    severity="info"
                                    class="whitespace-nowrap self-center mb-2"
                                    style="cursor: default"
                                >{{ t('profile-overview.product-card.purchased-amount', { quantity: item.quantity }, item.quantity) }}</Tag>
                                <FavoriteButton
                                    v-if="item.quantity === 0"
                                    filled
                                    :action="item.quantity === 0 ? 'remove' : 'add'"
                                    :product="item.productNo"
                                    @update="handleUpdate(item.productNo)"
                                    class="p-overlay-badge p-badge p-0"
                                />
                            </div>
                            <div class="flex flex-wrap items-center justify-between gap-2">
                                <BuyButton
                                    v-if="item.productNo !== BENCHMARK_JOBMATCH"
                                    :product="item.productNo"
                                    :product-name="item.productName"
                                    :trainer="trainer"
                                    :shop-link="item.shopLink"
                                    @update="handleUpdate(item.productNo)"
                                />
                                <ConfigureProject v-if="trainer" :product="item" :trainer="trainer" />
                            </div>
                        </div>
                    </OverlayBadge>
                </div>
            </template>
        </DataView>
    </div>
</template>


